
import React from 'react';
import { Shape, Text} from 'react-konva';
import { Globals } from './Globals.js';
var Point = require('./Point');
var numerical = require('./numerical');

const LINE_SIZE = 100.0;
const ROT_DEGREES = 36.0;

const RVA = 0;
const RVB = 1;
const RVC = 2;
const RVD = 3;
const RVE = 4;
const RVF = 5;
const RVG = 6;
const RVH = 7;
const RV_INVALID = -1;

const RHOMBUS_LABELS = [ 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const NUM_VERTICES = 4;
const APPROX_TOLERANCE = 0.2

const CLOCKWISE = 1;
const COUNTER_CLOCKWISE = -1;


const RHOMBUS_ANGLES = [ 
        36.0, 144.0, 36.0, 144.0,
        72.0, 108.0, 72.0, 108.0
];

const RHOMBUS_POINTS = [
        new Point(0.0,0.0), new Point(LINE_SIZE,0.0),
        new Point(LINE_SIZE*(1.0 + Math.cos(deg2rad(RHOMBUS_ANGLES[RVA]))), LINE_SIZE*Math.sin(deg2rad(RHOMBUS_ANGLES[RVA]))),
        new Point(LINE_SIZE*Math.cos(deg2rad(RHOMBUS_ANGLES[RVA])), LINE_SIZE*Math.sin(deg2rad(RHOMBUS_ANGLES[RVA]))),
        new Point(0.0,0.0), new Point(LINE_SIZE,0.0),
        new Point(LINE_SIZE*(1.0 + Math.cos(deg2rad(RHOMBUS_ANGLES[RVE]))), LINE_SIZE*Math.sin(deg2rad(RHOMBUS_ANGLES[RVE]))),
        new Point(LINE_SIZE*Math.cos(deg2rad(RHOMBUS_ANGLES[RVE])), LINE_SIZE*Math.sin(deg2rad(RHOMBUS_ANGLES[RVE])))
];


const ANGLE_TO_RVX = {144:RVB, 108:RVF, 72:RVE, 36:RVA};
const ORDERED_ANGLE_TO_RVX_INDICES = [144, 108, 72, 36];

const RHOMBUS_LINE_FROM_ANGLE = new Object();
RHOMBUS_LINE_FROM_ANGLE[CLOCKWISE] = [ 0.0, 36.0, -180.0, 360.0-144.0, 0.0, 72.0, -180.0, 360-108.0 ];
RHOMBUS_LINE_FROM_ANGLE[COUNTER_CLOCKWISE] = [ 36.0, 180.0, 36.0-180.0, 0.0, 72.0, 180.0, 72.0-180.0, 0.0 ];


function rad2deg(radians)
{
  var pi = Math.PI;
  return radians * (180/pi);
}

function deg2rad(degrees)
{
  var pi = Math.PI;
  return degrees * (pi/180);
}


class Rhombus extends React.Component {

  #vertices

  constructor( props) {
    super(props);
    this.#vertices = []
    
    // determine the rotation
    let globals = new Globals();
    let r = Point.fromObject(props.alignment).subtract(props.position).getAngle() - 
              RHOMBUS_LINE_FROM_ANGLE[globals.direction][props.vertex_id]

    // build the rhombus so that the vertex_id is at (0,0)
    let start = Math.floor(props.vertex_id / NUM_VERTICES)*NUM_VERTICES;
    for (let i=0; i<NUM_VERTICES; i++) {
      var p = Point.fromObject(RHOMBUS_POINTS[start+i]);
      p.subtract(RHOMBUS_POINTS[props.vertex_id]);
      p.rotate(r);
      p.add(props.position);
      this.#vertices.push(p);
    }
  }

  static assert( expression, text) {
    if (!expression) {
      throw new Error(text);
    }
  }

  static vidFix( vid, baseVid) {
    return (vid+NUM_VERTICES)%NUM_VERTICES + Math.floor(baseVid/NUM_VERTICES)*NUM_VERTICES;
  }

  static suggested_rhombus_vertex( angle) {
    this.assert( angle >= ROT_DEGREES, `Angle ${angle} is too small`);
    
    for (let a of ORDERED_ANGLE_TO_RVX_INDICES) {
      if (angle >= a) {
        return ANGLE_TO_RVX[a];
      }
    }
    this.assert(false,`Should not get here with angle=${angle}`);
  }

  get_vertices() {
    return Array.from(this.#vertices);
  }

  get_vertex(vid) {
    let vids = Math.floor(vid / NUM_VERTICES)*NUM_VERTICES;
    let pvids = Math.floor(this.props.vertex_id / NUM_VERTICES)*NUM_VERTICES;
    Rhombus.assert( vids === pvids, `vid=${vid} not in same group as vertex_id=${this.props.vertex_id}`);
    return this.#vertices[vid - pvids];
  }

  get_vid() {
    return this.props.vertex_id;
  }

  find_vertex_id(point) {
    for( let i=0; i< this.#vertices.length; i++) {
      if (this.#vertices[i].getDistance(point) <= APPROX_TOLERANCE) {
        return i + Math.floor(this.props.vertex_id / NUM_VERTICES)*NUM_VERTICES;
      }
    }
    return RV_INVALID;
  }

  vertex_crosses_through( vertex, pos) {
    let vid = this.find_vertex_id(pos);
    if (vid >= RVA) {
      // find the previous and next vertices around pos
      let i = vid - Math.floor(vid / NUM_VERTICES)*NUM_VERTICES;
      let cur = this.#vertices[i];
      let prev = this.#vertices[(vid+3) % NUM_VERTICES];
      let c2p = Point.fromObject(prev).subtract(cur);
      let angle_to_vertex = vertex.getDirectedAngle(c2p);
      if ((angle_to_vertex < (RHOMBUS_ANGLES[vid] - 1)) && (angle_to_vertex > 1)) {
        return true;
      }
    }
    return false;
  }

  is_in_vicinity(rhombus) {
    if (this.#vertices[0].getDistance(rhombus.#vertices[0]) > LINE_SIZE*4) {
      return false;
    }
    return true;
  }

  overlaps(rhombus) {
    // check first if the rhombuses are anywhere near each other
    if (!this.is_in_vicinity(rhombus)) {
      return false;
    }
    
    // check if any of the diagonals from each rhombus intersect.
    // if they do, then they are overlapping
    for (let vidx1 of [0,1]) {
      // use a diagonal that is slightly smaller than the original
      // so that adjacent rhombus vertexes aren't part of the intersection
      let v0 = this.#vertices[vidx1].clone().moveToward(this.#vertices[vidx1+2],APPROX_TOLERANCE);
      let x0 = v0.x;
      let y0 = v0.y;
      let v1 = this.#vertices[vidx1+2].clone().moveToward(this.#vertices[vidx1],APPROX_TOLERANCE);
      let x1 = v1.x;
      let y1 = v1.y;
      for (let vidx2 of [0,1]) {
        let v2 = rhombus.#vertices[vidx2].clone().moveToward(rhombus.#vertices[vidx2+2],APPROX_TOLERANCE);
        let x2 = v2.x;
        let y2 = v2.y;
        let v3 = rhombus.#vertices[vidx2+2].clone().moveToward(rhombus.#vertices[vidx2],APPROX_TOLERANCE);
        let x3 = v3.x;
        let y3 = v3.y;
        let p0 = (y3-y2)*(x3-x0)-(x3-x2)*(y3-y0)
        if (Math.abs(p0) < APPROX_TOLERANCE) { p0 = 0.0 };
        let p1 = (y3-y2)*(x3-x1)-(x3-x2)*(y3-y1)
        if (Math.abs(p1) < APPROX_TOLERANCE) { p1 = 0.0; }
        let p2 = (y1-y0)*(x1-x2)-(x1-x0)*(y1-y2)
        if (Math.abs(p2) < APPROX_TOLERANCE) { p2 = 0.0; }
        let p3 = (y1-y0)*(x1-x3)-(x1-x0)*(y1-y3)
        if (Math.abs(p3) < APPROX_TOLERANCE) { p3 = 0.0; }
        if ((p0 === 0.0) && (p1 === 0.0) && (p2 === 0.0) && (p3 === 0.0)) {
          // the lines are parallel
          if (x0 === x3) {
            if (!( ((Math.min(y2,y3) > Math.max(y0,y1)) && (Math.max(y0,y1) < Math.min(y2,y3))) || 
                   ((Math.min(y0,y1) > Math.max(y2,y3)) && (Math.max(y2,y3) < Math.min(y0,y1)))) ) {
              return true;
            }
          }
          if (y0 === y3) {
            if (!( ((Math.min(x2,x3) > Math.max(x0,x1)) && (Math.max(x0,x1) < Math.min(x2,x3))) ||
                   ((Math.min(x0,x1) > Math.max(x2,x3)) && (Math.max(x2,x3) < Math.min(x0,x1)))) ) {
              return true;
            }
          }
          break;
        }
        if ((p0*p1 <= 0.0) && (p2*p3 <= 0)) {
          return true
        }
      }
    }
    
    return false;
  }

  get_text_label_spot(vertex) {
    let vid1 = this.find_vertex_id(vertex);
    let vid2 = (vid1+2)%NUM_VERTICES + Math.floor(vid1 / NUM_VERTICES)*NUM_VERTICES;
    let v2 = this.get_vertex(vid2);
    return vertex.moveToward(v2,0.05);
  }

  render(index) {
    return (
      <React.Fragment key={index}>
        {Globals.DEBUG &&
         this.#vertices.map(item => (
           <Text key={RHOMBUS_LABELS[this.find_vertex_id(item)]}
                 text={RHOMBUS_LABELS[this.find_vertex_id(item)]}
                 x={this.get_text_label_spot(item).x} 
                 y={this.get_text_label_spot(item).y-4}/>
        ))}
        <Shape
          sceneFunc={(context, shape) => {
            context.beginPath();
            context.moveTo(this.#vertices[0].x, this.#vertices[0].y);
            context.lineTo(this.#vertices[1].x, this.#vertices[1].y);
            context.lineTo(this.#vertices[2].x, this.#vertices[2].y);
            context.lineTo(this.#vertices[3].x, this.#vertices[3].y);
            context.closePath();
            context.fillStrokeShape(shape);
          }}
          key={index}
          stroke="black"
        />
      </React.Fragment>
    );
  }
}

export { Rhombus, LINE_SIZE, NUM_VERTICES, APPROX_TOLERANCE, RVA, RVB, RVC, RVD, RVE, RVF, RVG, RVH,
         ANGLE_TO_RVX, ORDERED_ANGLE_TO_RVX_INDICES, ROT_DEGREES, CLOCKWISE, COUNTER_CLOCKWISE, RHOMBUS_ANGLES };
